// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import {
    MY_INVENTORY_LIST_VIEW_CLASS_NAME
} from "../../../../../core/constants/className";
import {
    T_COMMON_OPEN,
    T_DATE_FORMAT,
    T_MY_INVENTORY_LIST_VIEW_NO_CLIENT,
    T_MY_INVENTORY_LIST_VIEW_TITLE,
    T_MY_INVENTORY_LIST_VIEW_NO_RESULTS_FOUND,
    T_MY_INVENTORY_LIST_VIEW_TABLE_INVENTORY_ID_LABEL,
    T_MY_INVENTORY_LIST_VIEW_TABLE_TIME_LABEL,
    T_MY_INVENTORY_LIST_VIEW_TABLE_END_TIME_LABEL,
    T_MY_INVENTORY_LIST_VIEW_TABLE_SUMMARY_LABEL,
    T_MY_INVENTORY_LIST_VIEW_TABLE_STATE_LABEL
} from "../../../../../core/constants/translation";
import { useParams } from "react-router-dom";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { useCallback } from "react";
import { GET_MY_CLIENT_INVENTORY_ITEM_ROUTE } from "../../../../../core/constants/route";
import { RouteService } from "../../../../../../../io/hyperify/frontend/services/RouteService";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { Card } from "../../../../../../../io/hyperify/frontend/components/card/Card";
import { Loader } from "../../../../../../../io/hyperify/frontend/components/loader/Loader";
import { Table } from "../../../../../../../io/hyperify/frontend/components/table/Table";
import { TableHeader } from "../../../../../../../io/hyperify/frontend/components/table/TableHeader";
import { TableHeaderColumn } from "../../../../../../../io/hyperify/frontend/components/table/TableHeaderColumn";
import { TableBody } from "../../../../../../../io/hyperify/frontend/components/table/TableBody";
import { map } from "../../../../../../../io/hyperify/core/functions/map";
import { InventoryItemDTO } from "../../../../../../../io/hyperify/core/store/types/inventory/InventoryItemDTO";
import { TableRow } from "../../../../../../../io/hyperify/frontend/components/table/TableRow";
import { TextColumn } from "../../../../../../../io/hyperify/frontend/components/table/columns/text/TextColumn";
import { TimeColumn } from "../../../../../../../io/hyperify/frontend/components/table/columns/time/TimeColumn";
import { TableColumn } from "../../../../../../../io/hyperify/frontend/components/table/TableColumn";
import { Button } from "../../../../../../../io/hyperify/frontend/components/button/Button";
import { useMyInventoryList } from "../../../../hooks/useMyInventoryList";
import { getInventoryStateTranslationKey } from "../../../../../../../io/hyperify/core/store/constants/storeTranslation";
import { ButtonStyle } from "../../../../../../../io/hyperify/core/frontend/button/ButtonStyle";
import "./MyInventoryListView.scss";

const LOG = LogService.createLogger('MyInventoryListView');

export interface MyInventoryListProps {
    readonly className?: string;
    readonly t?: TranslationFunction;
}

export function MyInventoryListView (props: MyInventoryListProps) {
    const t: TranslationFunction = props?.t ?? ((key: string): string => key);
    const className = props?.className;
    const params = useParams();
    const clientId = params?.clientId;
    const [ list/*, refreshList*/ ] = useMyInventoryList(clientId);
    const isLoading = list?.length === undefined;
    const listCount = list?.length ?? 0;
    const onItemClick = useCallback(
        (itemId: string) => {
            const route = GET_MY_CLIENT_INVENTORY_ITEM_ROUTE(clientId ?? '', itemId);
            LOG.debug(`Going to inventory route: `, route);
            RouteService.setRoute(route);
        },
        [
            clientId
        ]
    );
    return (
        <div
            className={
                MY_INVENTORY_LIST_VIEW_CLASS_NAME
                + (className ? ` ${className}` : '')
            }
        >{!clientId ? (
            <p>{t(T_MY_INVENTORY_LIST_VIEW_NO_CLIENT)}</p>
        ) : (
            <>
                <h3 className={`${MY_INVENTORY_LIST_VIEW_CLASS_NAME}-title`}>{t(T_MY_INVENTORY_LIST_VIEW_TITLE)}</h3>
                <div className={`${MY_INVENTORY_LIST_VIEW_CLASS_NAME}-details`}>

                    <Card>

                        {isLoading ? <Loader /> : null}

                        {!isLoading && listCount === 0 ? (
                            <p>{t(T_MY_INVENTORY_LIST_VIEW_NO_RESULTS_FOUND)}</p>
                        ) : null}

                        {!isLoading && listCount !== 0 ?
                            <Table>
                                <TableHeader>
                                    <TableHeaderColumn>{t(T_MY_INVENTORY_LIST_VIEW_TABLE_INVENTORY_ID_LABEL)}</TableHeaderColumn>
                                    <TableHeaderColumn>{t(T_MY_INVENTORY_LIST_VIEW_TABLE_STATE_LABEL)}</TableHeaderColumn>
                                    <TableHeaderColumn>{t(T_MY_INVENTORY_LIST_VIEW_TABLE_TIME_LABEL)}</TableHeaderColumn>
                                    <TableHeaderColumn>{t(T_MY_INVENTORY_LIST_VIEW_TABLE_END_TIME_LABEL)}</TableHeaderColumn>
                                    <TableHeaderColumn>{t(T_MY_INVENTORY_LIST_VIEW_TABLE_SUMMARY_LABEL)}</TableHeaderColumn>
                                    <TableHeaderColumn>
                                    </TableHeaderColumn>
                                </TableHeader>
                                <TableBody>{map(
                                    list,
                                    (item: InventoryItemDTO) => {
                                        const inventoryItemId = item?.inventoryItemId;
                                        const itemClick = () => onItemClick(inventoryItemId);
                                        return (
                                            <TableRow
                                                key={`inventory-${inventoryItemId}`}
                                                click={itemClick}
                                            >
                                                <TextColumn value={inventoryItemId} />
                                                <TextColumn value={t(getInventoryStateTranslationKey(item?.state))} />
                                                <TimeColumn
                                                    value={item?.date}
                                                    format={t(T_DATE_FORMAT)}
                                                />
                                                <TimeColumn
                                                    value={item?.endDate}
                                                    format={t(T_DATE_FORMAT)}
                                                />
                                                <TextColumn value={item?.summary} />
                                                <TableColumn>
                                                    <Button
                                                        click={itemClick}
                                                        style={ButtonStyle.SECONDARY}
                                                    >{t(T_COMMON_OPEN)}</Button>
                                                </TableColumn>
                                            </TableRow>
                                        );
                                    }
                                )}
                                </TableBody>
                            </Table> : null
                        }

                    </Card>

                </div>
            </>
        )}</div>
    );
}
